import type { Ref } from 'react';
import { forwardRef } from 'react';

import type { BoxProps } from '@/components/Box';

import type { IconSize, IconVariant } from './Icon.types';
import { iconSet } from './icons';
import { IconPixelSize } from './utils';

import * as S from './Icon.styles';

export interface IconProps extends BoxProps {
  name: keyof typeof iconSet;
  size?: IconSize;
  variant?: IconVariant;
}

export const Icon = forwardRef(
  (
    { name, size = 'm', variant = 'regular', width, ...rest }: IconProps,
    ref?: Ref<HTMLDivElement>,
  ) => {
    const icon = iconSet?.[name]?.[variant]?.[size];

    if (!icon) {
      throw new Error(
        `There's no icon named '${name}', that is of variant '${variant}' and size of '${size}'`,
      );
    }

    const { code } = icon;
    const iconSize = IconPixelSize[size];

    return (
      <S.SVG
        ref={ref}
        aria-label={`${name}-icon`}
        height={`${iconSize}px`}
        minWidth={`${width ?? iconSize}px`}
        viewBox={`0 0 ${width ?? iconSize} ${iconSize}`}
        width={`${width ?? iconSize}px`}
        xmlns='http://www.w3.org/2000/svg'
        {...rest}
      >
        {code}
      </S.SVG>
    );
  },
);

Icon.displayName = 'Icon';
